import { Button, Card, Chip, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type SiteRow, type StaffMealInput, type StaffMealRow } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

interface MenuItemRow {
  id: string;
  name: string;
  kind?: string;
}

interface MealRow {
  id: string;
  name: string;
}

export function StaffMealsTab() {
  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });
  const meals = useQuery({
    queryKey: ["menu", "lists", "meal"],
    queryFn: () => api<MealRow[]>("/menu/lists/meal"),
  });
  const foods = useQuery({
    queryKey: ["menu", "items", "food"],
    queryFn: () => api<MenuItemRow[]>("/menu/items?kind=food"),
  });

  const kitchens = (sites.data ?? []).filter((s) => s.kind === "kitchen");
  const kitchenMap = new Map(kitchens.map((k) => [k.id, k.name]));
  const mealMap = new Map((meals.data ?? []).map((m) => [m.id, m.name]));
  const foodMap = new Map((foods.data ?? []).map((f) => [f.id, f.name]));

  const [selectedKitchenId, setSelectedKitchenId] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [showAdd, setShowAdd] = useState(false);

  // Form states
  const [date, setDate] = useState("1405-08-01");
  const [kitchenId, setKitchenId] = useState("");
  const [mealId, setMealId] = useState("");
  const [menuItemId, setMenuItemId] = useState("");
  const [portions, setPortions] = useState("5");
  const [note, setNote] = useState("");

  const staffMeals = useQuery({
    queryKey: ["inventory", "staff-meals", selectedKitchenId, selectedMonth],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedKitchenId && selectedKitchenId !== "all") params.set("kitchenId", selectedKitchenId);
      if (selectedMonth) params.set("month", selectedMonth);
      const q = params.toString() ? `?${params.toString()}` : "";
      return api<StaffMealRow[]>(`/inventory/staff-meals${q}`);
    },
  });

  const addAction = useAction(async () => {
    const kId = kitchenId || (kitchens[0]?.id ?? "");
    const mId = mealId || (meals.data?.[0]?.id ?? "");
    const fId = menuItemId || (foods.data?.[0]?.id ?? "");
    const count = parseInt(portions, 10);
    if (!kId || !mId || !fId || isNaN(count) || count <= 0) {
      throw new Error("لطفاً فیلدهای الزامی را به درستی وارد کنید");
    }

    const payload: StaffMealInput = {
      businessDate: date,
      kitchenId: kId,
      mealId: mId,
      menuItemId: fId,
      portions: count,
      ...(note.trim() ? { note: note.trim() } : {}),
    };

    await api("/inventory/staff-meals", {
      method: "POST",
      body: payload,
    });

    setShowAdd(false);
    setNote("");
  }, [
    ["inventory", "staff-meals"],
    ["inventory", "warehouses"],
  ]);

  const mealItems = staffMeals.data ?? [];
  const totalPortions = mealItems.reduce((sum, item) => sum + item.portions, 0);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="cluster" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div className="cluster" style={{ gap: "var(--s3)" }}>
          <div className="field">
            <label>آشپزخانه</label>
            <select
              value={selectedKitchenId}
              onChange={(e) => {
                setSelectedKitchenId(e.target.value);
              }}
              className="input"
            >
              <option value="all">همه آشپزخانه‌ها</option>
              {kitchens.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>ماه (مثال: ۱۴۰۵-۰۸)</label>
            <input
              type="text"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
              }}
              placeholder="همه ماه‌ها"
              className="input"
              style={{ width: "130px" }}
            />
          </div>
        </div>

        <Button
          variant="primary"
          onClick={() => {
            setShowAdd(!showAdd);
          }}
        >
          {showAdd ? "بستن فرم" : "ثبت مصرف پرسنل +"}
        </Button>
      </div>

      {showAdd && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">ثبت مصرف غذای پرسنل</h3>
            <div className="cluster" style={{ gap: "var(--s3)" }}>
              <div className="field">
                <label>تاریخ کاری *</label>
                <input
                  type="text"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                  }}
                  className="input"
                  dir="ltr"
                />
              </div>

              <div className="field">
                <label>آشپزخانه *</label>
                <select
                  value={kitchenId || (kitchens[0]?.id ?? "")}
                  onChange={(e) => {
                    setKitchenId(e.target.value);
                  }}
                  className="input"
                >
                  {kitchens.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>وعده غذایی *</label>
                <select
                  value={mealId || (meals.data?.[0]?.id ?? "")}
                  onChange={(e) => {
                    setMealId(e.target.value);
                  }}
                  className="input"
                >
                  {(meals.data ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>غذا *</label>
                <select
                  value={menuItemId || (foods.data?.[0]?.id ?? "")}
                  onChange={(e) => {
                    setMenuItemId(e.target.value);
                  }}
                  className="input"
                >
                  {(foods.data ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>تعداد پرس *</label>
                <input
                  type="number"
                  min="1"
                  value={portions}
                  onChange={(e) => {
                    setPortions(e.target.value);
                  }}
                  className="input"
                  style={{ width: "90px" }}
                />
              </div>
            </div>

            <div className="field">
              <label>توضیح / علت</label>
              <input
                type="text"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                placeholder="توضیح اختیاری..."
                className="input"
              />
            </div>

            <ErrorLine error={addAction.error} />

            <div className="cluster" style={{ justifyContent: "flex-end" }}>
              <Button
                variant="primary"
                onClick={() => {
                  addAction.run(undefined);
                }}
                disabled={addAction.busy}
              >
                {addAction.busy ? "در حال ثبت..." : "تأیید و صدور حواله مصرف"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="cluster" style={{ gap: "var(--s3)" }}>
        <Chip tone="ok">{`تعداد رکوردها: ${faDigits(mealItems.length)}`}</Chip>
        <Chip tone="blue">{`مجموع پرس پرسنل: ${faDigits(totalPortions)}`}</Chip>
      </div>

      <Card>
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>آشپزخانه</th>
              <th>وعده</th>
              <th>غذا</th>
              <th>تعداد پرس</th>
              <th>شماره سند حواله انبار</th>
              <th>یادداشت</th>
            </tr>
          </thead>
          <tbody>
            {mealItems.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--muted)" }}>
                  رکوردی برای مصرف پرسنل ثبت نشده است
                </td>
              </tr>
            ) : (
              mealItems.map((item) => (
                <tr key={item.id}>
                  <td dir="ltr">{faDigits(item.businessDate)}</td>
                  <td>{kitchenMap.get(item.kitchenId) ?? item.kitchenId.slice(0, 8)}</td>
                  <td>{mealMap.get(item.mealId) ?? item.mealId.slice(0, 8)}</td>
                  <td>{foodMap.get(item.menuItemId) ?? item.menuItemId.slice(0, 8)}</td>
                  <td>
                    <strong>{faDigits(item.portions)}</strong>
                  </td>
                  <td dir="ltr" style={{ fontSize: "var(--font-s)" }}>
                    {item.voucherId.slice(0, 13)}...
                  </td>
                  <td>{item.note || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
